import { useMemo } from "react";

import { useMatrix } from "~/context/MatrixContext";
import { useRooms } from "~/hooks/useRooms";
import { useThreadsFeed } from "~/hooks/useThreadsFeed";
import {
  getRoomNotificationLevel,
  type MentionFeedItem,
  type RoomSummary,
  type ThreadsFeedItem,
} from "~/lib/matrix";

type Sender = { id: string; displayName: string; avatarUrl: string | null };

export interface AttentionCounts {
  activityCount: number;
  threadsHasUnread: boolean;
  homeCount: number;
  appBadgeCount: number;
  settled: boolean;
}

export type ActivityRow =
  | {
      kind: "mention";
      id: string;
      roomId: string;
      roomName: string;
      body: string;
      sender: Sender;
      ts: number;
      unread: boolean;
    }
  | {
      kind: "reaction";
      id: string;
      roomId: string;
      roomName: string;
      emoji: string;
      targetEventId: string;
      sender: Sender;
      ts: number;
      unread: boolean;
    }
  | {
      kind: "thread";
      id: string;
      roomId: string;
      roomName: string;
      preview: string;
      sender: string;
      ts: number;
      unread: boolean;
    };

function isCountedRoom(room: RoomSummary, mutedRoomIds: Set<string>): boolean {
  return !room.isSpace && room.archived === null && !mutedRoomIds.has(room.id);
}

export function computeAttention(input: {
  rooms: RoomSummary[];
  threads: ThreadsFeedItem[];
  threadsSettled: boolean;
  mutedRoomIds: Set<string>;
  mentionEventIds: Set<string>;
}): { activityCount: number; homeCount: number; appBadgeCount: number } {
  const counted = input.rooms.filter((room) => isCountedRoom(room, input.mutedRoomIds));

  let highlights = 0;
  let home = 0;
  for (const room of counted) {
    highlights += room.unreadHighlight;
    if (room.isDirect) home += room.unreadTotal;
  }

  const countedRoomIds = new Set(counted.map((room) => room.id));
  let threadReplies = 0;
  for (const thread of input.threads) {
    if (!countedRoomIds.has(thread.roomId)) continue;
    if (thread.unreadReplyEventIds === undefined) {
      threadReplies += thread.unreadReplyCount;
      continue;
    }
    for (const eventId of thread.unreadReplyEventIds) {
      if (!input.mentionEventIds.has(eventId)) threadReplies += 1;
    }
  }

  const activityCount = highlights + (input.threadsSettled ? threadReplies : 0);
  return {
    activityCount,
    homeCount: home,
    appBadgeCount: home + activityCount,
  };
}

export function threadsHasUnread(threads: ThreadsFeedItem[], settled: boolean): boolean {
  return settled && threads.some((thread) => thread.unreadReplyCount > 0);
}

export function buildActivityFeed(input: {
  rooms: RoomSummary[];
  mentionsFeed: MentionFeedItem[];
  threads: ThreadsFeedItem[];
}): ActivityRow[] {
  const visibleRoomIds = new Set(input.rooms.map((room) => room.id));

  const coveredByThread = new Set<string>();
  for (const thread of input.threads) {
    if (thread.unreadReplyCount <= 0) continue;
    for (const eventId of thread.unreadReplyEventIds ?? []) coveredByThread.add(eventId);
  }

  const rows: ActivityRow[] = [];
  for (const item of input.mentionsFeed) {
    if (!visibleRoomIds.has(item.roomId)) continue;
    if (item.kind === "mention") {
      rows.push({
        kind: "mention",
        id: item.eventId,
        roomId: item.roomId,
        roomName: item.roomName,
        body: item.body,
        sender: item.sender,
        ts: item.ts,
        unread: !coveredByThread.has(item.eventId),
      });
      continue;
    }
    rows.push({
      kind: "reaction",
      id: item.eventId,
      roomId: item.roomId,
      roomName: item.roomName,
      emoji: item.emoji,
      targetEventId: item.targetEventId,
      sender: item.sender,
      ts: item.ts,
      unread: false,
    });
  }
  for (const thread of input.threads) {
    if (thread.unreadReplyCount <= 0) continue;
    rows.push({
      kind: "thread",
      id: thread.rootEventId,
      roomId: thread.roomId,
      roomName: thread.roomName,
      preview: thread.lastReplyBodyPreview,
      sender: thread.lastReplySender,
      ts: thread.lastReplyTs,
      unread: true,
    });
  }

  rows.sort((a, b) => b.ts - a.ts);
  return rows;
}

function mentionEventIds(feed: MentionFeedItem[]): Set<string> {
  const ids = new Set<string>();
  for (const item of feed) {
    if (item.kind === "mention") ids.add(item.eventId);
  }
  return ids;
}

function mutedRoomIds(
  client: NonNullable<ReturnType<typeof useMatrix>["client"]>,
  rooms: RoomSummary[],
) {
  const muted = new Set<string>();
  for (const room of rooms) {
    if (getRoomNotificationLevel(client, room.id) === "mute") muted.add(room.id);
  }
  return muted;
}

export function useAttention(): AttentionCounts {
  const { client, mentionsFeed } = useMatrix();
  const rooms = useRooms();
  const { items: threads, settled } = useThreadsFeed();

  const muteSet = useMemo(
    () => (client ? mutedRoomIds(client, rooms) : new Set<string>()),
    [client, rooms],
  );
  const mentions = useMemo(() => mentionEventIds(mentionsFeed ?? []), [mentionsFeed]);

  return useMemo(() => {
    const { activityCount, homeCount, appBadgeCount } = computeAttention({
      rooms,
      threads,
      threadsSettled: settled,
      mutedRoomIds: muteSet,
      mentionEventIds: mentions,
    });
    return {
      activityCount,
      homeCount,
      appBadgeCount,
      threadsHasUnread: threadsHasUnread(threads, settled),
      settled,
    };
  }, [rooms, threads, settled, muteSet, mentions]);
}

export function useActivityFeed(): ActivityRow[] {
  const { mentionsFeed } = useMatrix();
  const rooms = useRooms();
  const { items: threads } = useThreadsFeed();
  return useMemo(
    () => buildActivityFeed({ rooms, mentionsFeed: mentionsFeed ?? [], threads }),
    [rooms, mentionsFeed, threads],
  );
}
